import { Popup } from '@progress/kendo-react-popup'
import classNames from 'classnames'
import { type PrimitiveAtom, useAtomValue } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useHover } from 'react-use'

import { FlickeringBadge } from '~/components/elements/FlickeringBadge'
import { HStack } from '~/components/elements/HStack'
import {
  StyledButton,
  type StyledButtonProps,
} from '~/components/elements/StyledButton'
import { Typography } from '~/components/elements/Typography'
import { VStack } from '~/components/elements/VStack'
import { useTurnOnAtom } from '~/hooks/jotai'

import styles from './FilterInformation.css'

export type FilterInformationProps = {
  additionalContent?: React.ReactNode
  className?: string
  'data-testid'?: string
  editDialog: React.ReactNode
  filterDisplay: React.ReactNode
  isFiltered: boolean
  isVisibleEditDialogAtom: PrimitiveAtom<boolean>
  onClear?: () => void
}

export const FilterInformation = ({
  additionalContent,
  className,
  'data-testid': dataTestId = 'components-features-filter-information',
  editDialog,
  filterDisplay,
  isFiltered,
  isVisibleEditDialogAtom,
  onClear,
}: FilterInformationProps) => {
  const { t } = useTranslation()
  const openEditDialog = useTurnOnAtom(isVisibleEditDialogAtom)
  const isVisibleEditDialog = useAtomValue(isVisibleEditDialogAtom)

  const anchor = React.useRef<HTMLDivElement>(null)
  const handleFilterClearButtonClick = React.useCallback<
    EH<StyledButtonProps, 'onClick'>
  >(() => {
    onClear?.()
  }, [onClear])

  const [editButton] = useHover((hovered) => (
    <div ref={anchor}>
      <StyledButton fillMode="flat" icon="filter" onClick={openEditDialog}>
        {t('filter_settings')}

        {isFiltered && <FlickeringBadge />}
      </StyledButton>

      {isFiltered && (
        <Popup
          anchor={anchor.current}
          anchorAlign={{ horizontal: 'center', vertical: 'bottom' }}
          animate={false}
          className={styles.popup}
          popupAlign={{ horizontal: 'left', vertical: 'top' }}
          show={hovered}
        >
          <VStack padding={4} spacing={4}>
            <VStack spacing={2}>
              <Typography fontWeight={700} variant="normal">
                {t('specified_filter')}
              </Typography>

              {filterDisplay}
            </VStack>

            <StyledButton
              className={styles.filterClearButton}
              fillMode="flat"
              onClick={handleFilterClearButtonClick}
            >
              {t('remove_filter')}
            </StyledButton>
          </VStack>
        </Popup>
      )}
    </div>
  ))

  return (
    <HStack
      align="center"
      className={classNames(styles.root, className)}
      data-testid={dataTestId}
      spacing={4}
    >
      {additionalContent}

      {editButton}

      {isVisibleEditDialog && editDialog}
    </HStack>
  )
}
